import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab
} from '@mui/material';
import {
  CompareArrows as CompareIcon
} from '@mui/icons-material';
import { useSearchParams, useLocation } from 'react-router-dom';

// Import the individual page components
import TransactionComparisonByDate from './TransactionComparisonByDate';
import TransactionComparisonDetailedView from './TransactionComparisonDetailed';
import TransactionComparisonUnifiedView from './TransactionComparisonUnified';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transaction-comparison-tabpanel-${index}`}
      aria-labelledby={`transaction-comparison-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `transaction-comparison-tab-${index}`,
    'aria-controls': `transaction-comparison-tabpanel-${index}`,
  };
}

const TransactionComparison: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  
  // Tab names mapping
  const tabNames = ['by-date', 'detailed', 'unified'];
  const tabNameToIndex: { [key: string]: number } = {
    'by-date': 0,
    'detailed': 1,
    'unified': 2
  };
  
  // Determine initial tab based on route or URL param
  let initialTab = 0;
  const tabParam = searchParams.get('tab');
  if (tabParam) {
    // Check if it's a named tab
    if (tabNameToIndex.hasOwnProperty(tabParam.toLowerCase())) {
      initialTab = tabNameToIndex[tabParam.toLowerCase()];
    } else {
      // Fallback: try to parse as number for backwards compatibility
      const tabNum = parseInt(tabParam, 10);
      if (!isNaN(tabNum) && tabNum >= 0 && tabNum < tabNames.length) {
        initialTab = tabNum;
        // Update URL to use named tab
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('tab', tabNames[tabNum]);
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  } else if (location.pathname.includes('detailed')) {
    initialTab = 1;
  } else if (location.pathname.includes('unified')) {
    initialTab = 2;
  }
  
  const [tabValue, setTabValue] = useState(initialTab);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Update URL with named tab instead of number
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabNames[newValue]);
    setSearchParams(newSearchParams, { replace: true });
  };

  // Sync tab value with URL param or route
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      // Check if it's a named tab
      if (tabNameToIndex.hasOwnProperty(tabParam.toLowerCase())) {
        setTabValue(tabNameToIndex[tabParam.toLowerCase()]);
      } else {
        // Fallback: try to parse as number for backwards compatibility
        const tabNum = parseInt(tabParam, 10);
        if (!isNaN(tabNum) && tabNum >= 0 && tabNum < tabNames.length) {
          setTabValue(tabNum);
          // Update URL to use named tab
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('tab', tabNames[tabNum]);
          setSearchParams(newSearchParams, { replace: true });
        }
      }
    } else if (location.pathname.includes('detailed')) {
      setTabValue(1);
      // Update URL to include named tab param
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', 'detailed');
      setSearchParams(newSearchParams, { replace: true });
    } else if (location.pathname.includes('unified')) {
      setTabValue(2);
      // Update URL to include named tab param
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', 'unified');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, location.pathname, setSearchParams]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <CompareIcon sx={{ mr: 2 }} />
          Transaction Comparison
        </Typography>
      </Box>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="transaction comparison tabs"
            variant="fullWidth"
          >
            <Tab label="By Date (Paginated)" {...a11yProps(0)} />
            <Tab label="Detailed (Grouped by Date)" {...a11yProps(1)} />
            <Tab label="Unified (With Matching)" {...a11yProps(2)} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {tabValue === 0 && <TransactionComparisonByDate />}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {tabValue === 1 && <TransactionComparisonDetailedView />}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {tabValue === 2 && <TransactionComparisonUnifiedView />}
        </TabPanel>
      </Card>
    </Box>
  );
};

export default TransactionComparison;
